// =============================================================================
// MeuDriver — useCancellation (Batch J4)
// =============================================================================
// Hooks para política de cancelamento e reembolso automático.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { bookingKeys } from '@/hooks/useBookings'
import { useAuth } from '@/hooks/useAuth'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface CancellationPreview {
  refund_percentage: number
  refund_amount: number
  hours_until: number
  policy_name: string
}

export interface CancellationResult {
  success: boolean
  cancellation_log_id: string
  refund_percentage: number
  refund_amount: number
  new_status: string
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const cancellationKeys = {
  preview: (bookingId: string) => ['cancellation', 'preview', bookingId] as const,
}

// ---------------------------------------------------------------------------
// Hooks públicos
// ---------------------------------------------------------------------------

/**
 * Calcula o preview de reembolso para um booking específico.
 * Mostra ao usuário antes de confirmar o cancelamento.
 */
export function useCancellationPreview(bookingId: string) {
  return useQuery<CancellationPreview, Error>({
    queryKey: cancellationKeys.preview(bookingId),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('calculate_refund_amount', {
        p_booking_id: bookingId,
      })

      if (error) throw new Error(error.message)

      return data as unknown as CancellationPreview
    },
    enabled: Boolean(bookingId),
    staleTime: 1000 * 60, // cache de 1 min — horas_até muda com o tempo
  })
}

/**
 * Cancela um booking aplicando a política de reembolso.
 * Se refund_amount > 0, chama a edge function admin-refund para processar
 * o reembolso no Mercado Pago.
 */
export function useCancelBooking() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation<CancellationResult, Error, {
    bookingId: string
    reason?: string
    cancelledBy: 'student' | 'instructor'
  }>({
    mutationFn: async ({ bookingId, reason, cancelledBy }) => {
      // 1. Processa cancelamento + calcula reembolso
      const { data, error } = await supabase.rpc('process_cancellation', {
        p_booking_id:   bookingId,
        p_reason:       reason ?? '',
        p_cancelled_by: cancelledBy,
      })

      if (error) throw new Error(error.message)

      const result = data as unknown as CancellationResult

      // 2. Dispara reembolso no MP se houver valor a reembolsar
      if (result.refund_amount > 0) {
        try {
          await supabase.functions.invoke('admin-refund', {
            body: {
              booking_id: bookingId,
              amount: result.refund_amount,
              reason: reason ?? 'Cancelamento conforme política',
            },
          })
        } catch (refundErr) {
          // Reembolso é best-effort — o cancelamento já foi registrado.
          // Log para auditoria manual se necessário.
          console.warn('[useCancelBooking] admin-refund error:', refundErr)
        }
      }

      return result
    },
    onSuccess: (_data, { bookingId }) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(bookingId) })
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.student(user.id) })
        queryClient.invalidateQueries({ queryKey: bookingKeys.instructor(user.id) })
      }
      queryClient.invalidateQueries({ queryKey: cancellationKeys.preview(bookingId) })
    },
  })
}
